import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";

import { api } from "helpers/api_url";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";

import { fetchStockOverview } from "slices/Inventory/Inventory/Stock_Overview/thunk";

import {
  SortingState,
  ColumnFiltersState,
  OnChangeFn,
} from "@tanstack/react-table";

// =============================
// INTERFACE
// =============================
interface StockOverViewProps {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  filterValue: any[];
  search: string;
  sortField: string;
  sortDirection: "asc" | "desc" | "";

  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setFilterValue: (filters: any[]) => void;
}

const StockOverView: React.FC<StockOverViewProps> = ({
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  sortField,
  sortDirection,

  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
}) => {
  document.title = "Stock Overview";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // ================================
  // SELECTOR (Same structure as InStock)
  // ================================
  const selectStockOverview = createSelector(
    (state: any) => state.InventoryStockReducer,
    (item) => ({
      list: item?.data || [],
      rowCount: item?.totalRecords || 0,
      loading: item?.loading,
      error: item?.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectStockOverview);

  // ================================
  // FETCH API (Same pattern as InStock)
  // ================================
  useEffect(() => {
    const payload = {
      page: pageIndex + 1,
      pageSize,
      search,
      sort: sortField,
      order: sortDirection,
      filters: filterValue,
    };

    dispatch(fetchStockOverview(payload));
  }, [
    dispatch,
    pageIndex,
    pageSize,
    search,
    sortField,
    sortDirection,
    filterValue,
  ]);

  // ================================
  // ACTION COLUMN
  // ================================
  const DynamicAction = [
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => (
        <ul className="d-flex gap-2 list-unstyled mb-0">
          <li>
            <button
              className="btn btn-sm"
              style={{ backgroundColor: "#3969E7", color: "#FFF" }}
              onClick={() =>
                navigate(
                  `/view-stockoverview-in-stock/${row.original.master_item_id}`
                )
              }
            >
              View
            </button>
          </li>
        </ul>
      ),
    },
  ];

  // ================================
  // TABLE COLUMNS (Same style as InStock)
  // ================================
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: any) => {
          const pIndex = table.getState().pagination.pageIndex;
          const pSize = table.getState().pagination.pageSize;
          return pIndex * pSize + row.index + 1;
        },
      },

      {
        header: "Item Name",
        accessorKey: "item_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/item_name`,
        cell: ({ row }: { row: any }) => row.original?.item_name || "-",
      },

      {
        header: "Category",
        accessorKey: "category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/category_name`,
      },

      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/sub_category_name`,
      },

      {
        header: "Unit",
        accessorKey: "unit_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/unit_name`,
      },

      {
        header: "Total Quantity",
        accessorKey: "total_available_quantity",
        enableSorting: false,
        enableColumnFilter: false,
      },

      ...DynamicAction,
    ],
    [list]
  );

  // ================================
  // RENDER
  // ================================
  return (
    <Card className="p-3">
      <LoaderErrorWrapper loading={loading} error={error}>
        <TableContainer2
          columns={columns}
          data={list}
          totalRecords={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageChange={setPageIndex}
          onPageSizeChange={setPageSize}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          colValue={setFilterValue}
          isPagination={true}
          manualSorting={true}
          isGlobalFilter={true}
          SearchPlaceholder="Search Items..."
        />
      </LoaderErrorWrapper>
    </Card>
  );
};

export default StockOverView;
